import jsPDF from 'jspdf'
import 'jspdf-autotable'
import { wtGetFromLocalStorage } from 'src/helpers/commons/LocalStorage'
import { currentDateTime, wtFormatDate } from 'src/helpers/commons/moment'
import { lzNotifyErrorMessageFromResult } from 'src/helpers/commons/notifications'
import { wtFormatMoney } from 'src/helpers/commons/utils'
import { TYPE_TOTAL } from 'src/models/pnlConfig'

const user = wtGetFromLocalStorage('authUser')
const SOURCE_TYPE_GP_REPORT_CONFIG_DETAIL = 2

const isTruthy = (value) => value === true || value === 1 || value === '1'

const isLabelRow = (row) => isTruthy(row?.is_label)

const isPercentageRow = (row) => isTruthy(row?.is_percentage)

const isShowOnPdfRow = (row) => isTruthy(row?.show_on_pdf)

const isShowOnVerticalAnalysisRow = (row) => isTruthy(row?.show_on_vertical_analysis)

const isShowOnHorizontalAnalysisRow = (row) => isTruthy(row?.show_on_horizontal_analysis)

const formatPdfValue = (value, row) => {
  if (isLabelRow(row)) {
    return ''
  }

  if (isPercentageRow(row)) {
    return `${Number(value || 0).toFixed(2)} %`
  }

  return wtFormatMoney(Number(value || 0), '', true, 2)
}

const formatFormulaValue = (value, row) => {
  if (isPercentageRow(row)) {
    return `${Number(value || 0).toFixed(2)} %`
  }

  return wtFormatMoney(Number(value || 0), '', true, 2)
}

const getChangeValue = (row) => Number(row?.current_year_balance || 0) - Number(row?.last_year_balance || 0)

const getPercentChangeValue = (row) => {
  const currentValue = Number(row?.current_year_balance || 0)
  const lastValue = Number(row?.last_year_balance || 0)

  if (lastValue === 0) {
    return currentValue === 0 ? 0 : null
  }

  return ((currentValue - lastValue) / Math.abs(lastValue)) * 100
}

const formatPercentChange = (row) => {
  const percentChange = getPercentChangeValue(row)
  if (percentChange === null) {
    return '-'
  }

  return `${percentChange.toFixed(2)} %`
}

const flattenRows = (rows = []) => {
  let flattenedRows = []

  rows.forEach((row) => {
    flattenedRows.push(row)
    if (Array.isArray(row.children) && row.children.length > 0) {
      flattenedRows = flattenedRows.concat(flattenRows(row.children))
    }
  })

  return flattenedRows
}

const getRowValue = (row, key) => Number(row?.[key] || 0)

const getSourceRow = (source, rowsById) => (
  Number(source.source_type || 0) === SOURCE_TYPE_GP_REPORT_CONFIG_DETAIL
    ? rowsById[String(source.source_id)] || null
    : null
)

const getSourceName = (source) => source.source_name || source.name || ''

const getFormulaTerm = (source, isFirstTerm = false) => {
  const operator = source.operator || '+'
  const name = getSourceName(source)

  if (operator === '%') {
    return `${isFirstTerm ? '' : '+ '}(${Number(source.percentage_value || 0)}% of ${name})`
  }

  if (isFirstTerm && operator === '+') {
    return name
  }

  return `${operator} ${name}`
}

const getFormulaText = (row) => {
  const sources = Array.isArray(row.children) ? row.children : []
  if (sources.length <= 0) {
    return row.name || ''
  }

  const formula = sources.map((source, index) => getFormulaTerm(source, index === 0)).join(' ')
  const multiplyByValue = Number(row.multiply_by_value || 0)
  const divideByValue = Number(row.divide_by_value || 0)
  const valueDivideByFormula = Number(row.value_divide_by_formula || 0)

  let formulaText = formula
  if (multiplyByValue > 0) {
    formulaText = `(${formulaText}) * ${multiplyByValue}`
  }
  if (divideByValue > 0) {
    formulaText = `(${formulaText}) / ${divideByValue}`
  }
  if (valueDivideByFormula > 0) {
    formulaText = `${valueDivideByFormula} / (${formulaText})`
  }

  return formulaText
}

const getSourceValue = (source, rowsById, key) => {
  const sourceValue = getRowValue(source, key)
  if (sourceValue !== 0) {
    return sourceValue
  }

  const sourceRow = getSourceRow(source, rowsById)
  if (sourceRow) {
    return getRowValue(sourceRow, key)
  }

  return sourceValue
}

const getFormulaValues = (row, rowsById, key) => {
  const sources = Array.isArray(row.children) ? row.children : []
  if (sources.length <= 0) {
    return []
  }

  return sources.map((source) => {
    const sourceRow = getSourceRow(source, rowsById) || source
    return {
      name: getSourceName(source),
      value: formatFormulaValue(getSourceValue(source, rowsById, key), sourceRow),
    }
  })
}

const getFormulaValuesText = (row, rowsById, key) => getFormulaValues(row, rowsById, key)
  .map((source) => `${source.name} = ${source.value}`)
  .join('\n')

const getPrintableRows = (rows = []) => rows.filter((row) => isShowOnPdfRow(row))

const getVerticalAnalysisRows = (rows = []) => rows.filter((row) => isShowOnVerticalAnalysisRow(row))

const getHorizontalAnalysisRows = (rows = []) => rows.filter((row) => isShowOnHorizontalAnalysisRow(row))

const getFormulaRows = (rows = []) => rows.filter((row) => (
  isShowOnPdfRow(row) &&
  !isLabelRow(row) &&
  Array.isArray(row.children) &&
  row.children.length > 0
))

const normalizeText = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()

const getRowByPatterns = (rows, patterns) => {
  const normalizedPatterns = patterns.map((pattern) => normalizeText(pattern))

  return rows.find((row) => {
    const rowName = normalizeText(row?.name)
    return normalizedPatterns.some((pattern) => rowName.includes(pattern))
  }) || null
}

const getRowDirection = (row) => {
  if (!row) {
    return 'stable'
  }

  const currentValue = Number(row.current_year_balance || 0)
  const lastValue = Number(row.last_year_balance || 0)

  if (currentValue > lastValue) {
    return 'up'
  }

  if (currentValue < lastValue) {
    return 'down'
  }

  return 'stable'
}

const formatExecutiveMetric = (row) => {
  if (!row) {
    return ''
  }

  return `${formatPdfValue(row.current_year_balance, row)} vs ${formatPdfValue(row.last_year_balance, row)} last year`
}

const buildExecutiveSummarySections = (rows) => {
  const revenueRow = getRowByPatterns(rows, ['Revenue - net', 'net sales', 'sales net'])
  const grossProfitMarginRow = getRowByPatterns(rows, ['Gross profit margin', 'gross margin'])
  const operatingProfitMarginRow = getRowByPatterns(rows, ['Operating profit margin', 'operating margin'])
  const profitBeforeTaxMarginRow = getRowByPatterns(rows, ['Profit before tax margin', 'pbt margin'])
  const profitAfterTaxMarginRow = getRowByPatterns(rows, ['Profit after taxation margin', 'profit after tax margin', 'pat margin'])
  const currentRatioRow = getRowByPatterns(rows, ['Current ratio'])
  const quickRatioRow = getRowByPatterns(rows, ['Quick ratio', 'acid test ratio'])
  const inventoryTurnoverRow = getRowByPatterns(rows, ['Inventory turnover'])
  const inventoryHoldingDaysRow = getRowByPatterns(rows, ['Inventory holding days', 'inventory days'])
  const roeRow = getRowByPatterns(rows, ['Return on equity', 'roe'])
  const roceRow = getRowByPatterns(rows, ['Return on capital employed', 'roce'])
  const fixedAssetReturnRow = getRowByPatterns(rows, ['Fixed asset return', 'fixed assets return', 'fixed asset turnover'])
  const assetTurnoverRow = getRowByPatterns(rows, ['Asset turnover', 'total asset turnover'])

  const overallPerformance = revenueRow && getRowDirection(revenueRow) === 'down'
    ? `Revenue softened year on year at ${formatExecutiveMetric(revenueRow)}, and the decline in earnings indicates that the business closed the period under visible commercial and operating pressure. Overall performance remains profitable, but the trend is weaker than last year and needs close management attention.`
    : 'Overall performance remained profitable during the year, although the broader ratio trend points to softer momentum and tighter operating conditions than last year. Management attention is needed to protect earnings quality and restore growth discipline.'

  const profitabilityAnalysis = `Profitability weakened across the board. Cost of sales consumed a higher share of net sales, which compressed gross margins and flowed through to lower operating profit, profit before tax, and profit after tax margins. The decline in ${grossProfitMarginRow ? 'gross' : ''}${grossProfitMarginRow && operatingProfitMarginRow ? ', operating' : ''}${profitAfterTaxMarginRow ? ', and net' : ''} margins suggests margin pressure is no longer limited to one part of the income statement; it is affecting the full earnings chain.`

  const liquidityPosition = `Liquidity also tightened during the period. ${currentRatioRow ? `The current ratio moved to ${formatPdfValue(currentRatioRow.current_year_balance, currentRatioRow)}` : 'The current ratio declined'}${quickRatioRow ? ` and the quick ratio moved to ${formatPdfValue(quickRatioRow.current_year_balance, quickRatioRow)}` : ' and the quick ratio also declined'}, indicating lower short-term flexibility and a reduced buffer against working capital stress. This raises the importance of disciplined cash flow monitoring and faster conversion of stock and receivables into cash.`

  const efficiencyWorkingCapital = `Efficiency indicators deteriorated. Inventory turnover slowed${inventoryHoldingDaysRow ? ` while inventory holding days increased to ${formatPdfValue(inventoryHoldingDaysRow.current_year_balance, inventoryHoldingDaysRow)}` : ' and inventory holding days increased significantly'}, pointing to inventory buildup and slower stock movement. At the same time, returns on capital and assets declined, with softer performance in ROE, ROCE, fixed asset return, and asset turnover, which signals lower asset utilization and weaker productivity of invested capital.`

  const keyConcerns = [
    'Margin pressure is reducing earnings conversion from sales and is directly lowering operating and net profitability.',
    'Working capital is becoming more heavily tied up in inventory, increasing cash flow strain and the risk of slow-moving stock.',
    'Lower asset utilization is reducing returns on invested funds and weakening overall capital efficiency.',
  ]

  const recommendations = [
    'Review cost of sales drivers and pricing strategy to recover margin in key product and service lines.',
    'Tighten inventory planning, identify slow-moving stock early, and strengthen replenishment discipline to release working capital.',
    'Strengthen weekly working capital and cash flow monitoring, including focused review of inventory, receivables, and short-term funding needs.',
    'Improve asset utilization and operational efficiency through department-wise variance analysis and management accountability reviews.',
  ]

  return [
    { heading: 'Overall Performance', text: overallPerformance },
    { heading: 'Profitability Analysis', text: profitabilityAnalysis },
    { heading: 'Liquidity Position', text: liquidityPosition },
    { heading: 'Efficiency & Working Capital', text: efficiencyWorkingCapital },
    { heading: 'Key Concerns', bullets: keyConcerns },
    { heading: 'Recommendations', bullets: recommendations },
  ]
}

const Pdf = async (ratioAnalysisData, asOf, lastYearLastDate, options = {}) => {
  const reportRows = Array.isArray(ratioAnalysisData) ? ratioAnalysisData : []
  const printableRows = getPrintableRows(reportRows)
  const includeExecutiveSummary = options.includeExecutiveSummary !== false

  if (printableRows.length <= 0) {
    lzNotifyErrorMessageFromResult('No printable data found!')
    return
  }

  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  })

  const totalPagesExp = '{total_pages_count_string}'
  const logo = 'assets/wil-icon.png'
  const printedAt = currentDateTime()
  const pageWidth = doc.internal.pageSize.width ? doc.internal.pageSize.width : doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.height ? doc.internal.pageSize.height : doc.internal.pageSize.getHeight()
  const statementNoteColumnWidth = 10
  const statementFirstColumnWidth = 55
  const statementNumericColumnWidth = 28
  const statementChangeColumnWidth = 24
  const statementPercentChangeColumnWidth = 20
  const currentYearColor = [15, 61, 117]
  const lastYearColor = [22, 101, 52]
  const labelColor = [29, 78, 216]
  const darkBlue = [15, 61, 117]
  const formulaTableWidth = 178
  const formulaTableMarginLeft = (pageWidth - formulaTableWidth) / 2
  const statementUsableWidth = pageWidth - 20
  const allRowsById = flattenRows(reportRows).reduce((rowsById, row) => {
    rowsById[String(row.id)] = row
    return rowsById
  }, {})

  const header = (title) => {
    doc.setFontSize(11)
    doc.setTextColor('#3c4b64')
    doc.setFont('Helvetica', 'bold')
    doc.addImage(logo, 'PNG', 5, 4, 25, 25)
    doc.line(32, 6, 32, 25)
    doc.text('WAH', 35, 10, { align: 'left' })
    doc.text('INDUSTRIES', 35, 15)
    doc.text('LIMITED', 35, 20)
    doc.setFontSize(9)
    doc.text('www.wahindustries.com', 35, 25)
    doc.setFontSize(11)
    doc.text(title, pageWidth - 10, 15, { align: 'right' })
    doc.text('AS OF ' + wtFormatDate(asOf, 'MMMM D, Y'), pageWidth - 10, 22, { align: 'right' })
    doc.line(10, 30, pageWidth - 10, 30)
  }

  const footer = () => {
    const pageNo = doc.internal.getNumberOfPages()
    const footerLineY = pageHeight - 15
    const footerMetaY = pageHeight - 5

    doc.setFontSize(10)
    doc.setTextColor('black')
    doc.setFont('Helvetica', 'normal')
    doc.line(14, footerLineY, pageWidth - 14, footerLineY)
    drawPrintedAt()

    let pageText = 'Page ' + pageNo
    if (typeof doc.putTotalPages === 'function') {
      pageText = pageText + ' of ' + totalPagesExp
    }

    doc.setFontSize(9)
    doc.text(pageText, pageWidth - 10, footerMetaY, { align: 'right' })
    doc.text('Printed By ', 20, footerMetaY, { align: 'left' })
    doc.setTextColor('blue')
    doc.text(user ? user.name : '', 37, footerMetaY, { align: 'left' })
  }

  const statementHeader = (title) => {
    doc.setFontSize(11)
    doc.setTextColor('#3c4b64')
    doc.setFont('Helvetica', 'bold')
    doc.addImage(logo, 'PNG', 5, 4, 25, 25)
    doc.line(32, 6, 32, 25)
    doc.text('WAH', 35, 10, { align: 'left' })
    doc.text('INDUSTRIES', 35, 15)
    doc.text('LIMITED', 35, 20)
    doc.setFontSize(9)
    doc.text('www.wahindustries.com', 35, 25)
    doc.setFontSize(11)
    doc.text(title, pageWidth - 75, 15, { align: 'left' })
    doc.text('AS OF ' + wtFormatDate(asOf, 'MMMM D, Y'), pageWidth - 75, 22, { align: 'left' })
    doc.line(10, 30, pageWidth - 10, 30)
  }

  const drawPrintedAt = () => {
    const printedText = 'This report is printed through ERP at '
    const printedTextWidth = doc.getTextWidth(printedText)
    const printedAtWidth = doc.getTextWidth(printedAt)
    const printedTextX = (pageWidth - printedTextWidth - printedAtWidth) / 2

    doc.setFontSize(10)
    doc.setTextColor('black')
    doc.setFont('Helvetica', 'normal')
    doc.text(printedText, printedTextX, pageHeight - 20)
    doc.setTextColor('#0202f7')
    doc.setFont('Helvetica', 'bold')
    doc.text(printedAt, printedTextX + printedTextWidth, pageHeight - 20)
  }

  const formulaRows = getFormulaRows(reportRows)
  const verticalAnalysisRows = getVerticalAnalysisRows(reportRows)
  const horizontalAnalysisRows = getHorizontalAnalysisRows(reportRows)
  const noteNumbersById = formulaRows.reduce((notes, row, index) => {
    notes[String(row.id)] = index + 1
    return notes
  }, {})

  if (includeExecutiveSummary) {
    const executiveSummarySections = buildExecutiveSummarySections(printableRows)

    header('RATIO ANALYSIS EXECUTIVE SUMMARY')

    let summaryY = 38
    executiveSummarySections.forEach((section) => {
      doc.setFont('Helvetica', 'bold')
      doc.setFontSize(11)
      doc.setTextColor(...darkBlue)
      doc.text(section.heading, 14, summaryY)
      summaryY += 5

      doc.setFont('Helvetica', 'normal')
      doc.setFontSize(9.3)
      doc.setTextColor(33, 37, 41)

      if (Array.isArray(section.bullets)) {
        section.bullets.forEach((bullet) => {
          const bulletLines = doc.splitTextToSize(`• ${bullet}`, pageWidth - 28)
          doc.text(bulletLines, 18, summaryY)
          summaryY += (bulletLines.length * 4.1) + 1
        })
      } else {
        const sectionLines = doc.splitTextToSize(section.text, pageWidth - 24)
        doc.text(sectionLines, 14, summaryY)
        summaryY += (sectionLines.length * 4.1) + 2
      }
    })

    footer()
    doc.addPage()
  }

  const buildSectionBody = (rows, includeVariance = false, includeNotes = false, includePercentChange = false, percentChangeAlign = 'right') => {
    let hasActiveLabelInSection = false

    return rows.map((row) => {
      const isLabel = isLabelRow(row)
      const indentText = hasActiveLabelInSection && !isLabel ? '      ' : ''
      const changeValue = getChangeValue(row)
      const percentChange = formatPercentChange(row)

      if (isLabel) {
        hasActiveLabelInSection = true
      }

      const bodyRow = [
        {
          content: `${indentText}${row.name || ''}`,
          styles: {
            fontStyle: row.type === TYPE_TOTAL || isLabel ? 'bold' : 'normal',
            textColor: isLabel ? labelColor : [17, 24, 39],
          },
        },
      ]

      if (includeNotes) {
        bodyRow.push({
          content: noteNumbersById[String(row.id)] || '',
          styles: { halign: 'center', fontStyle: row.type === TYPE_TOTAL ? 'bold' : 'normal', textColor: darkBlue },
        })
      }

      bodyRow.push(
        {
          content: formatPdfValue(row.current_year_balance, row),
          styles: { halign: 'right', fontStyle: row.type === TYPE_TOTAL ? 'bold' : 'normal', textColor: currentYearColor },
        },
        {
          content: formatPdfValue(row.last_year_balance, row),
          styles: { halign: 'right', fontStyle: row.type === TYPE_TOTAL ? 'bold' : 'normal', textColor: lastYearColor },
        },
      )

      if (includeVariance) {
        bodyRow.push(
          {
            content: formatPdfValue(changeValue, row),
            styles: { halign: 'right', fontStyle: row.type === TYPE_TOTAL ? 'bold' : 'normal', textColor: changeValue >= 0 ? currentYearColor : [194, 65, 12] },
          },
          {
            content: isLabel ? '' : percentChange,
            styles: { halign: percentChangeAlign, fontStyle: row.type === TYPE_TOTAL ? 'bold' : 'normal', textColor: percentChange === '-' ? [107, 114, 128] : changeValue >= 0 ? currentYearColor : [194, 65, 12] },
          },
        )
      } else if (includePercentChange) {
        bodyRow.push({
          content: isLabel ? '' : percentChange,
          styles: { halign: percentChangeAlign, fontStyle: row.type === TYPE_TOTAL ? 'bold' : 'normal', textColor: percentChange === '-' ? [107, 114, 128] : changeValue >= 0 ? currentYearColor : [194, 65, 12] },
        })
      }

      return bodyRow
    })
  }

  const renderSectionTable = (
    title,
    rows,
    includeVariance = false,
    includeNotes = false,
    includePercentChange = false,
    percentChangeAlign = 'right',
    useStatementStyle = false,
    startY = null,
    includePageHeader = true,
  ) => {
    if (rows.length <= 0) {
      return null
    }

    const numericColumnCount = includeVariance ? 4 : includePercentChange ? 3 : 2
    const statementStyleFirstColumnWidth = useStatementStyle
      ? (includeVariance ? 58 : 78)
      : statementFirstColumnWidth
    const statementStyleNumericColumnWidth = useStatementStyle
      ? (statementUsableWidth - statementStyleFirstColumnWidth) / numericColumnCount
      : statementNumericColumnWidth

    const headRow = [
      { content: '', styles: { halign: 'center', cellWidth: useStatementStyle ? statementStyleFirstColumnWidth : statementFirstColumnWidth } },
    ]

    if (includeNotes) {
      headRow.push({ content: 'Note', styles: { halign: 'center', cellWidth: statementNoteColumnWidth, textColor: darkBlue } })
    }

    headRow.push(
      { content: 'Current Year', styles: { halign: 'center', cellWidth: useStatementStyle ? statementStyleNumericColumnWidth : statementNumericColumnWidth, textColor: currentYearColor } },
      { content: 'Last Year', styles: { halign: 'center', cellWidth: useStatementStyle ? statementStyleNumericColumnWidth : statementNumericColumnWidth, textColor: lastYearColor } },
    )

    if (includeVariance) {
      headRow.push(
        { content: 'Change', styles: { halign: 'center', cellWidth: useStatementStyle ? statementStyleNumericColumnWidth : statementChangeColumnWidth, textColor: darkBlue } },
        { content: '% Change', styles: { halign: 'center', cellWidth: useStatementStyle ? statementStyleNumericColumnWidth : statementPercentChangeColumnWidth, textColor: darkBlue } },
      )
    } else if (includePercentChange) {
      headRow.push(
        { content: '% Change', styles: { halign: 'center', cellWidth: useStatementStyle ? statementStyleNumericColumnWidth : statementPercentChangeColumnWidth, textColor: darkBlue } },
      )
    }

    const columnStyles = {
      0: { cellWidth: useStatementStyle ? statementStyleFirstColumnWidth : statementFirstColumnWidth, halign: 'left' },
    }

    let columnIndex = 1
    if (includeNotes) {
      columnStyles[columnIndex] = { cellWidth: statementNoteColumnWidth, halign: 'center' }
      columnIndex++
    }

    columnStyles[columnIndex] = { cellWidth: useStatementStyle ? statementStyleNumericColumnWidth : statementNumericColumnWidth, halign: 'right' }
    columnStyles[columnIndex + 1] = { cellWidth: useStatementStyle ? statementStyleNumericColumnWidth : statementNumericColumnWidth, halign: 'right' }

    if (includeVariance) {
      columnStyles[columnIndex + 2] = { cellWidth: useStatementStyle ? statementStyleNumericColumnWidth : statementChangeColumnWidth, halign: 'right' }
      columnStyles[columnIndex + 3] = { cellWidth: useStatementStyle ? statementStyleNumericColumnWidth : statementPercentChangeColumnWidth, halign: percentChangeAlign }
    } else if (includePercentChange) {
      columnStyles[columnIndex + 2] = { cellWidth: useStatementStyle ? statementStyleNumericColumnWidth : statementPercentChangeColumnWidth, halign: percentChangeAlign }
    }

    doc.autoTable({
      head: [headRow],
      body: buildSectionBody(rows, includeVariance, includeNotes, includePercentChange, percentChangeAlign),
      theme: 'plain',
      styles: {
        font: 'helvetica',
        fontSize: useStatementStyle ? 10 : 9,
        cellPadding: useStatementStyle
          ? { top: 1.2, right: 1, bottom: 1.2, left: 1 }
          : { top: 1.4, right: 1, bottom: 1.4, left: 1 },
      },
      headStyles: { fillColor: [255, 255, 255], textColor: [60, 75, 100], fontStyle: 'bold', fontSize: useStatementStyle ? 11 : 9.5 },
      columnStyles,
      margin: { top: 40, bottom: 25, left: 18, right: 10 },
      startY: startY || undefined,
      didDrawPage: function () {
        if (includePageHeader) {
          if (useStatementStyle) {
            statementHeader(title)
          } else {
            header(title)
          }
          footer()
        }
      },
      willDrawCell: function (data) {
        if (data.row.section === 'head' && data.column.index > 0) {
          const { cell } = data
          doc.setDrawColor(0, 0, 0)
          doc.setLineWidth(0.6)
          doc.line(cell.x + 2, cell.y + cell.height, cell.x + cell.width - 2, cell.y + cell.height)
        }
      },
    })

    return doc.lastAutoTable?.finalY || startY || 40
  }

  let hasRenderedDetailSection = false
  const renderPagedSection = (title, rows, includeVariance = false, includeNotes = false, includePercentChange = false, percentChangeAlign = 'right', useStatementStyle = false) => {
    if (rows.length <= 0) {
      return
    }

    if (hasRenderedDetailSection) {
      doc.addPage()
    }

    renderSectionTable(title, rows, includeVariance, includeNotes, includePercentChange, percentChangeAlign, useStatementStyle)
    hasRenderedDetailSection = true
  }

  const renderCombinedAnalysisPage = () => {
    if (verticalAnalysisRows.length <= 0 && horizontalAnalysisRows.length <= 0) {
      return
    }

    if (hasRenderedDetailSection) {
      doc.addPage()
    }

    statementHeader('VERTICAL & HORIZONTAL ANALYSIS')
    footer()

    let nextY = 38

    if (verticalAnalysisRows.length > 0) {
      nextY = (renderSectionTable('', verticalAnalysisRows, false, false, true, 'center', true, nextY, false) || nextY) + 8
    }

    if (horizontalAnalysisRows.length > 0) {
      doc.setFont('Helvetica', 'bold')
      doc.setFontSize(11)
      doc.setTextColor(...darkBlue)
      doc.text('Horizontal Analysis', 18, nextY)
      nextY += 3

      renderSectionTable('', horizontalAnalysisRows, true, false, false, 'center', true, nextY, false)
    }

    hasRenderedDetailSection = true
  }

  renderCombinedAnalysisPage()
  renderPagedSection('RATIO ANALYSIS', printableRows, true, true, false, 'right', false)

  if (formulaRows.length > 0 && hasRenderedDetailSection) {
    doc.addPage()
  }
  if (formulaRows.length > 0) {
    formulaRows.forEach((row, index) => {
      const formulaBody = [
        [
          {
            content: `${noteNumbersById[String(row.id)]}. ${row.name || ''}`,
            colSpan: 3,
            styles: { fillColor: [241, 245, 249], textColor: [17, 24, 39], fontStyle: 'bold', fontSize: 10, cellPadding: 2 },
          },
        ],
        [
          { content: 'Formula', styles: { fontStyle: 'bold', textColor: [60, 75, 100] } },
          { content: getFormulaText(row), colSpan: 2, styles: { fontStyle: 'bold', textColor: darkBlue } },
        ],
        [
          { content: 'Current Year', styles: { fontStyle: 'bold', textColor: [60, 75, 100] } },
          { content: getFormulaValuesText(row, allRowsById, 'current_year_balance') || '-', styles: { fontSize: 10, textColor: currentYearColor } },
          { content: formatPdfValue(row.current_year_balance, row), styles: { halign: 'right', fontStyle: 'bold', textColor: currentYearColor } },
        ],
        [
          { content: 'Last Year', styles: { fontStyle: 'bold', textColor: [60, 75, 100] } },
          { content: getFormulaValuesText(row, allRowsById, 'last_year_balance') || '-', styles: { fontSize: 10, textColor: lastYearColor } },
          { content: formatPdfValue(row.last_year_balance, row), styles: { halign: 'right', fontStyle: 'bold', textColor: lastYearColor } },
        ],
      ]

      const currentFormulaValues = getFormulaValues(row, allRowsById, 'current_year_balance')
      const lastYearFormulaValues = getFormulaValues(row, allRowsById, 'last_year_balance')
      const maxValueLines = Math.max(currentFormulaValues.length, lastYearFormulaValues.length, 1)
      const estimatedNoteHeight = 24 + (maxValueLines * 5.2)
      let nextStartY = index === 0 ? 40 : (doc.lastAutoTable?.finalY || 40) + 5

      if (nextStartY + estimatedNoteHeight > pageHeight - 25) {
        doc.addPage()
        nextStartY = 40
      }

      doc.autoTable({
        head: index === 0 || doc.lastAutoTable?.finalY === undefined ? [[
          { content: 'Particulars', styles: { halign: 'left' } },
          { content: 'Values', styles: { halign: 'left' } },
          { content: 'Ratio Value', styles: { halign: 'right' } },
        ]] : undefined,
        body: formulaBody,
        theme: 'grid',
        styles: { font: 'helvetica', fontSize: 9.5, cellPadding: 1.8, overflow: 'linebreak', valign: 'top', lineColor: [226, 232, 240], lineWidth: 0.1 },
        headStyles: { fillColor: [60, 75, 100], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 10 },
        columnStyles: {
          0: { cellWidth: 30 },
          1: { cellWidth: 100 },
          2: { cellWidth: 48, halign: 'right' },
        },
        margin: { top: 40, bottom: 25, left: formulaTableMarginLeft, right: formulaTableMarginLeft },
        startY: nextStartY,
        didDrawPage: function () {
          header('RATIO ANALYSIS FORMULAS')
          footer()
        },
      })
    })
  }

  if (typeof doc.putTotalPages === 'function') {
    doc.putTotalPages(totalPagesExp)
  }

  doc.save('Ratio_Analysis.pdf')
}

export default Pdf
